import { 
  createContext,
  Dispatch,
  ReactNode, 
  SetStateAction, 
  useContext,
  useEffect,
  useState
} from 'react'
import { 
  createTheme,
  CssBaseline, 
  ThemeProvider as TP 
} from '@mui/material'
import { Theme as ThemeType } from '@emotion/react'

import { useAuth } from './auth'

interface ThemeProviderProps {
  children: ReactNode; 
  theme?: ThemeType 
}

interface ThemeContext {
  mode: 'light' | 'dark'
  setMode: Dispatch<SetStateAction<"light" | "dark">>
}

const Theme = createContext({} as ThemeContext)

export default function ThemeProvider({ 
  children, 
  theme 
}: ThemeProviderProps) {
  const { user } = useAuth()
  const [mode, setMode] = useState<'light' | 'dark'>('light')

  const colors = mode === 'light'
    ? {
      background: '#edf1f4',
      paper: '#ffffff',
      surface: 'rgba(255, 255, 255, 0.92)',
      text: '#14293d',
      muted: '#556879',
      line: 'rgba(20, 41, 61, 0.12)',
      accent: '#b67943',
      accentStrong: '#c9894d',
      accentDark: '#8b5e34',
      primaryContrast: '#fffaf4',
      secondary: '#14293d',
      secondaryContrast: '#fffaf4',
      headerBackground: 'rgba(251, 252, 253, 0.82)',
      hover: 'rgba(20, 41, 61, 0.07)',
      selected: 'rgba(182, 121, 67, 0.12)',
      disabledBackground: 'rgba(20, 41, 61, 0.08)',
      inputBackground: 'rgba(255, 255, 255, 0.88)',
      shadow: '0 18px 52px rgba(17, 24, 39, 0.08)',
      bodyBackground: [
        'radial-gradient(circle at 85% 75%, rgba(194, 138, 82, 0.18), transparent 22%)',
        'radial-gradient(circle at 50% 40%, rgba(255, 255, 255, 0.92), transparent 42%)',
        'linear-gradient(180deg, #fbfcfd 0%, #edf1f4 72%)'
      ].join(', ')
    }
    : {
      background: '#081f39',
      paper: '#0d2b4a',
      surface: 'rgba(255, 255, 255, 0.12)',
      text: '#eef4fb',
      muted: '#c6d1dc',
      line: 'rgba(255, 255, 255, 0.2)',
      accent: '#c28a52',
      accentStrong: '#dda066',
      accentDark: '#8b5e34',
      primaryContrast: '#0a2038',
      secondary: '#eef4fb',
      secondaryContrast: '#0a2038',
      headerBackground: 'rgba(8, 25, 47, 0.76)',
      hover: 'rgba(255, 255, 255, 0.08)',
      selected: 'rgba(194, 138, 82, 0.18)',
      disabledBackground: 'rgba(255, 255, 255, 0.1)',
      inputBackground: 'rgba(255, 255, 255, 0.1)',
      shadow: '0 24px 80px rgba(0, 0, 0, 0.28)',
      bodyBackground: [
        'radial-gradient(circle at 85% 75%, rgba(194, 138, 82, 0.28), transparent 22%)',
        'radial-gradient(circle at 50% 40%, rgba(255, 255, 255, 0.07), transparent 42%)',
        'linear-gradient(180deg, #0a2746 0%, #081f39 72%)'
      ].join(', ')
    }
  
  theme = createTheme({
    palette: {
      mode,
      primary: {
        main: colors.accent,
        light: colors.accentStrong,
        dark: colors.accentDark,
        contrastText: colors.primaryContrast
      },
      secondary: {
        main: colors.secondary,
        contrastText: colors.secondaryContrast
      },
      background: {
        default: colors.background,
        paper: colors.paper
      },
      text: {
        primary: colors.text,
        secondary: colors.muted
      },
      divider: colors.line,
      action: {
        hover: colors.hover,
        selected: colors.selected,
        disabledBackground: colors.disabledBackground
      }
    },
    typography: {
      fontFamily: '"Avenir Next", Avenir, "Segoe UI", Arial, sans-serif',
      button: {
        fontWeight: 800,
        letterSpacing: 0,
        textTransform: 'uppercase'
      },
      h6: {
        fontWeight: 800,
        letterSpacing: 0
      }
    },
    shape: {
      borderRadius: 8
    },
    transitions: {
      duration: {
        shortest: 150,
        shorter: 200,
        short: 250,
        standard: 300,
        complex: 375,
        enteringScreen: 225,
        leavingScreen: 195
      },
      easing: {
        easeInOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
        easeOut: 'cubic-bezier(0.0, 0, 0.2, 1)',
        easeIn: 'cubic-bezier(0.4, 0, 1, 1)',
        sharp: 'cubic-bezier(0.4, 0, 0.6, 1)'
      },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            minHeight: '100vh',
            color: colors.text,
            backgroundColor: colors.background,
            backgroundImage: colors.bodyBackground
          },
          '#root': {
            minHeight: '100vh'
          }
        }
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            color: colors.text,
            backgroundColor: colors.headerBackground,
            borderBottom: `1px solid ${colors.line}`,
            backdropFilter: 'blur(12px)',
            boxShadow: 'none'
          }
        }
      },
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 999,
            fontWeight: 800,
            letterSpacing: 0,
            boxShadow: 'none',
            '&:hover': {
              boxShadow: 'none'
            }
          },
          containedPrimary: {
            color: colors.primaryContrast,
            backgroundColor: colors.accent,
            '&:hover': {
              backgroundColor: colors.accentStrong
            }
          },
          containedSecondary: {
            color: colors.secondaryContrast,
            backgroundColor: colors.secondary
          },
          outlinedPrimary: {
            borderColor: colors.accent,
            color: colors.accent
          },
          textPrimary: {
            color: colors.accent
          }
        }
      },
      MuiFab: {
        styleOverrides: {
          root: {
            color: colors.primaryContrast,
            backgroundColor: colors.accent,
            boxShadow: colors.shadow,
            '&:hover': {
              backgroundColor: colors.accentStrong
            }
          }
        }
      },
      MuiCard: {
        styleOverrides: {
          root: {
            color: colors.text,
            backgroundColor: colors.surface,
            border: `1px solid ${colors.line}`,
            borderRadius: 8,
            boxShadow: colors.shadow,
            backgroundImage: 'none'
          }
        }
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            color: colors.text,
            backgroundImage: 'none'
          }
        }
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            backgroundColor: colors.inputBackground,
            borderRadius: 8,
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: colors.line
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: colors.accent
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: colors.accent
            }
          }
        }
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            color: colors.muted,
            '&.Mui-focused': {
              color: colors.accent
            }
          }
        }
      },
      MuiListItemButton: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            '&:hover': {
              backgroundColor: colors.hover
            }
          }
        }
      },
      MuiListItemIcon: {
        styleOverrides: {
          root: {
            color: colors.muted
          }
        }
      },
      MuiDivider: {
        styleOverrides: {
          root: {
            borderColor: colors.line
          }
        }
      },
      MuiCircularProgress: {
        styleOverrides: {
          root: {
            color: colors.accent
          }
        }
      }
    }
  })

  useEffect(() => {
    if (user && user.theme) setMode(user.theme)
  // eslint-disable-next-line
  }, [user])

  return <Theme.Provider value={{
    mode,
    setMode
  }}>
    <TP theme={theme}>
      <CssBaseline />
      {children}
    </TP>
  </Theme.Provider>
}

export const useTheme = () => useContext(Theme)
